<template>
  <div>
    <button v-show="showBotChat" :class="{ 'keyboard-open': isKeyboardOpen === true }" class="vehicle-trim-start"
      @click="openOrCloseBot()">
      <span class="button-text">Trim Assistant</span>
      <img src="../../assets/images/icon/speech.png" />
    </button>
    <div :class="['vehicle-trim-container', chatContainerClasses]">
      <div class="vehicle-trim-overlay__window">
        <div id="chat-container" class="chat-container">
          <div class="chat-header">
            <div>
              <div class="header-logo__logo header-logo__bot-logo"></div>
            </div>
            <h2 class="header-text">Trim Assistant</h2>
            <button class="close-btn trimbot-btn" @click="openOrCloseBot(false)">
              <div class="close-btn-text">x</div>
            </button>
          </div>
          <div class="chat-messages" ref="chatMessages" :class="{ 'chat-messages-shrink': showComplaintOptions }"
            id="chat-messages">
            <div v-if="CreatingCustomMessage" class="message-mask">
            </div>
            <div v-for="(message, index) in messages" :key="index" class="message"
              :class="[message.class, { 'in-progress': message.generating }]">
              <vue-markdown :source="message.content" />
            </div>
            <div v-if="possibleResponses.some((x) => true)" class="answer-container">
              <button v-for="(answer, index) in possibleResponses" :key="index" class="response-btn trimbot-btn"
                @click="chooseTrim(answer)">
                {{ answer }}
              </button>
            </div>
          </div>
          <div v-if="!CreatingCustomMessage && showComplaintOptions && !ratingSent" class="complaint-container"
            :class="{ 'show-complaints': showComplaintOptions }">
            <RateTrimBotComponent :selectedScore="selectedScore" :ratingSent="ratingSent"
              @update:selectedScore="updateSelectedScore" @rate="sendRating" />
            <div class="complaint-button-container">
            </div>
          </div>

          <div class="chat-input">
            <textarea ref="chatbotTextbox" type="text" v-model="chatText" placeholder="Type a message..."
              :disabled="!showBotChat" @keyup.enter="SubmitMessageBox" />
            <button class="send-button" @click="SubmitMessageBox" :disabled="!showBotChat || chatText.length === 0">
              <div class="send-img">
                <submitButton :buttonDisabled="!showBotChat || chatText.length === 0" />
                </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang='ts'>
import Vue, { nextTick } from 'vue';
import IVehicle from '@/models/vehicle/interfaces/vehicle.d';
import IRange from '@/models/vehicle/interfaces/range.d';
import Touchpoint from '@/models/Touchpoint';
import { scrollService } from '@/services/ScrollService';
import { Component, Prop, Ref } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import VueMarkdown from 'vue-markdown-render';
import { store } from '@/store';
import { TrackingService, EventTypeEnum } from '@/services/TrackingService';
import { confettiService } from '@/services/ConfettiService';
import { trimChatbotService } from '@/services/TrimChatbotService';
// eslint-disable-next-line
import EnvironmentService from '@/services/EnvironmentService';
import ChatBotType from '@/enums/ChatBotType';
import GenericTouchPointComponent from '../ui/GenericTouchPointComponent.vue';
import GenericTouchPointTextComponent from '../ui/GenericTouchPointTextComponent.vue';
import VehicleTrimBotSubmitComponent from './VehicleTrimBotSubmitComponent.vue';
import KeyValue from '../../models/KeyValue';
import RateTrimBotComponent from './RateTrimBotComponent.vue'; // Import the new component

const VehicleSearchModule = namespace('vehicleSearchModule');

@Component({
  components: {
    touchpoint: GenericTouchPointComponent,
    touchpointText: GenericTouchPointTextComponent,
    submitButton: VehicleTrimBotSubmitComponent,
    VueMarkdown,
    RateTrimBotComponent // Register the new component
  }
})
export default class VehicleTrimBotComponent extends Vue {
  @Prop() private scrollToTrimLabel!: () => void;

  private conversationId: string = crypto.randomUUID();

  public selectedScore: number = 0;

  public scoreSent: boolean = false;

  public CreatingCustomMessage = false;

  @VehicleSearchModule.Getter public trims!: string[] | null;

  public chatOpen: boolean = false;

  public closing: boolean = false;

  public showComplaintOptions: boolean = false;

  public showBotChat: boolean = false;

  public awaitingResponse: boolean = false;

  public chatText: string = '';

  public messages: any[] = [];

  public isKeyboardOpen: boolean = false;

  private ratingSent: boolean = false;

  @Ref('chatbotTextbox')
  public readonly chatbotTextbox!: HTMLTextAreaElement;

  public possibleResponses: string[] = [];

  private getCorrelationId(): string {
    return this.$store.getters['initialSettingsModule/correlationId'];
  }

  private get apiEnv(): string {
    return EnvironmentService.getEnv();
  }

  public get currentRange(): IRange {
    return this.$store.getters['vehicleModule/currentRange'];
  }

  public get currentVehicle(): IVehicle {
    return this.$store.getters['vehicleModule/currentVehicle'];
  }

  public options: Touchpoint[] = [
    new Touchpoint('true', 'Yes'),
    new Touchpoint('false', 'No')
  ];

  public complaintOptions: KeyValue[] = [
    new KeyValue('1', 'I’ve been provided with incorrect data!'),
    new KeyValue('2', 'The bot was rude to me.'),
    new KeyValue('3', 'The conversation was too wordy')
  ];

  public startChat(): void {
    this.messages.push({ class: 'bot-message', content: 'Hi there! 👋 Welcome to Askaprice. I\'m the Trim Bot, here to help you find the perfect trim for your car. Whether you\'re interested in features like leather seats, satellite navigation, or reversing cameras, I\'ve got you covered. If you\'re not sure where to start, just let me know, and I\'ll guide you through the options. What features are you most interested in?' });
    this.showBotChat = true;
    scrollService.disableBodyScroll();
  }

  public async sendComplaint(complaintType: string, message: string): Promise<void> {
    this.openOrCloseBot();
    this.showBotChat = false;
    this.messages.push({ class: 'bot-message', content: 'Thank you for your feedback. We will look into this.' });
    this.CreatingCustomMessage = false;
    this.scrollDown();
    await trimChatbotService.sendComplaint(complaintType, message, this.getCorrelationId());
  }

  public async sendRating(rating: number) {
    this.scoreSent = true;
    await trimChatbotService.sendRating(
      rating,
      this.getCorrelationId()
    );
    this.messages.push({
      class: 'bot-message',
      content: rating === 5 ? 'Thank you your feedback! We are glad that the Trim Bot was able to help you.' : 'Thank you for your feedback. It is incredibly helpful in improving our service.'
    });
    this.scrollDown();
    this.ratingSent = true;
    if (rating === 5) {
      setTimeout(() => {
        const chatMessagesDiv = this.$refs.chatMessages as HTMLElement;
        confettiService.startConfetti(chatMessagesDiv, { height: chatMessagesDiv.scrollHeight });
      }, 300);
    }
  }

  public updateSelectedScore(rating: number) {
    this.selectedScore = rating;
  }

  public async sendMessage(): Promise<void> {
    this.awaitingResponse = true;

    this.possibleResponses = [];

    const consumerText = this.chatText.trim();
    this.chatText = '';

    this.messages.push({ class: 'user-message', content: consumerText });
    this.scrollDown();

    const responseMessage = { class: 'bot-message', content: '', generating: true };
    this.messages.push(responseMessage);

    await trimChatbotService.sendMessage(
      consumerText,
      parseInt(this.currentRange.rangeExternalReferenceId, 10),
      this.getCorrelationId(),
      this.conversationId,
      false,
      ChatBotType.Trimbot,
      (token) => {
        responseMessage.content += token;
        this.scrollDown();
      }
    );

    this.awaitingResponse = false;
    responseMessage.generating = false;

    if (this.trims && responseMessage.content) {
      this.possibleResponses = this.trims.filter((tr) => responseMessage.content.toLowerCase().indexOf(` ${tr.toLowerCase()} `) > -1
        || responseMessage.content.toLowerCase().indexOf(`**${tr.toLowerCase()}**`) > -1);
    }
    this.showComplaintOptions = true;
    this.scrollDown();
  }

  public chooseTrim(trim: string) {
    this.$emit('trimSelected', trim);
    this.openOrCloseBot(false);
  }

  public SubmitMessageBox() {
    scrollService.disableBodyScroll();
    if (!this.CreatingCustomMessage) {
      this.sendMessage();
      return;
    }
    // 99 is the enum for custom complaint message
    this.sendComplaint('99', this.chatText);
  }

  public scrollDown(): void {
    this.$nextTick(() => {
      const nestedElement = document.getElementById('chat-messages');

      if (nestedElement === null) {
        throw new Error();
      }

      nestedElement.scrollTo(0, nestedElement.scrollHeight);
    });
  }

  public async openOrCloseBot(open: boolean | null = null): Promise<void> {
    const shouldOpen = open !== null ? open : !this.chatOpen;

    if (shouldOpen) {
      this.chatOpen = true;
      this.showBotChat = true;
      if (this.messages.length === 0) {
        this.messages.push({ class: 'bot-message', content: 'Hi there! 👋 Welcome to Askaprice. I\'m the Trim Bot, here to help you find the perfect trim for your car. Whether you\'re interested in features like leather seats, satellite navigation, or reversing cameras, I\'ve got you covered. If you\'re not sure where to start, just let me know, and I\'ll guide you through the options. What features are you most interested in?' });
      } else {
        scrollService.disableBodyScroll();
      }
      // Waits for animation to end, then focuses on textbox
      setTimeout(() => {
        if (this.chatbotTextbox) {
          this.chatbotTextbox.focus();
        }
      }, 500);
      await TrackingService.instance.sendEvent(
        EventTypeEnum.TrimbotLoaded,
        store.getters['initialSettingsModule/correlationId'],
        null
      );
    } else {
      this.closing = true;
      scrollService.allowBodyScroll();
      setTimeout(() => {
        this.chatOpen = false;
        this.closing = false;
      }, 490);
      if (this.scrollToTrimLabel) {
        this.scrollToTrimLabel();
      }
    }
  }

  public get chatContainerClasses() {
    return {
      'vehicle-trim-container--open': this.chatOpen && !this.closing,
      'vehicle-trim-container--closing': this.closing,
      'vehicle-trim-container--closed': !this.chatOpen && !this.closing
    };
  }

  public handleResize() {
    this.isKeyboardOpen = window.innerHeight < 320;
  }

  beforeDestroy() {
    scrollService.allowBodyScroll();
    window.removeEventListener('resize', this.handleResize);
  }

  mounted() {
    window.addEventListener('resize', this.handleResize);
  }
}
</script>
